<template>
  <div class="pagination">
    <ul class="pagination__list">
      <li v-for="i in pageIndexes" :key="i" class="pagination__list-item">
        <!-- 🔴 D-238: the label said `{{ i }}` beside a digit of `{{ i + 1 }}`, so the
             button DISPLAYING "1" announced "Change to page 0" — off by one on every
             button, in both lists, and invisible to a sighted reviewer because
             `.is-sr-only` correctly hides it.

             🔑 THE LABEL IS THE BUG, NOT THE DIGIT. `i` is 0-based on purpose (see
             `pageIndexes` and the LP-3/MR-77 note): the `change-page` emit and the
             `currentPage === i` comparison both consume it that way, so "fixing" the
             index would break paging to fix an announcement. `i + 1` is the number a
             human calls this page, and it is already what renders.

             D-238 also adds `aria-current`: the active page was signalled by COLOUR
             ALONE (1.4.1), so a screen-reader user had no way to know where they were. -->
        <button
          class="pagination__button"
          :class="{ 'pagination__button--active': currentPage === i }"
          :style="buttonStyles(currentPage === i)"
          :aria-current="currentPage === i ? 'page' : null"
          @click="$emit('change-page', i)"
        >
          <span class="is-sr-only">Change to page {{ i + 1 }}</span>
          <span aria-hidden="true">{{ i + 1 }}</span>
        </button>
      </li>

      <!-- Show more pages -->
      <li v-if="count > 3" class="pagination__list-item pagination__list-item--more">
        <!-- D-238: a disclosure that never said whether it was open. `aria-expanded` is
             the whole contract of this control, and the §1.2 census recorded zero of them
             in the tree. 🪤 The sr-only name stays STATIC ("Expand more pages") rather
             than flipping to "Collapse": `aria-expanded` already carries the state, and a
             name that changes as well makes AT say the state twice, once of them stale on
             the element that already has focus (the D-234 problem).

             🔴 D-268: the disclosure also carries `aria-current="page"` WHEN COLLAPSED and
             the active page lives in the more-group (`currentPage >= 3`). D-238 put
             `aria-current` only on the sublist buttons, which sit in a `v-show` the Gallery
             collapses on every page change — so for any page past 3 the only current-page
             marker was on a `display:none` node, invisible to AT, and the 1.4.1 gap stayed
             open. The sublist keeps its marker while EXPANDED, so exactly ONE aria-current
             exists in every state and it is always on a VISIBLE element. -->
        <button
          class="pagination__button pagination__button--more"
          :class="{ 'pagination__button--active': currentPage >= 3 }"
          :style="buttonStyles(currentPage >= 3)"
          :aria-expanded="showMorePages ? 'true' : 'false'"
          :aria-current="currentPage >= 3 && !showMorePages ? 'page' : null"
          @click="toggleMorePages"
        >
          <span class="is-sr-only">Expand more pages</span>
          <span aria-hidden="true">•••</span>
        </button>

        <ul v-show="showMorePages" :class="moreClasses">
          <li v-for="i in countIndexes" :key="i" class="more__list-item more__list-item--sub">
            <!-- D-238, the same off-by-one in the sublist: it rendered "Page 4" and
                 announced "page 3". Same fix, same `aria-current`. -->
            <button
              v-if="i > 2"
              class="more__button"
              :class="{ 'more__button--active': currentPage === i }"
              :style="moreButtonStyles(currentPage === i)"
              :aria-current="currentPage === i && showMorePages ? 'page' : null"
              @click="$emit('change-page', i)"
            >
              <span class="is-sr-only">Change to page {{ i + 1 }}</span>
              <span aria-hidden="true">Page {{ i + 1 }}</span>
            </button>
          </li>
        </ul>
        <!-- /Show more pages -->
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'pagination',
  props: {
    theme: {
      type: Object,
      validator(value) {
        return ['light', 'dark', 'custom'].includes(value.type);
      },
    },
    count: {
      type: Number,
      default() {
        return 0;
      },
    },
    currentPage: {
      type: Number,
      default() {
        return 0;
      },
    },
  },
  data() {
    return {
      showMorePages: false,
    };
  },
  computed: {
    pages() {
      return this.count <= 3 ? this.count : 3;
    },
    // LP-3 (MR-77) number-range off-by-one fix: Vue1's `v-for="i in <Number>"`
    // was 0-based (0..n-1); Vue3's is 1-based (1..n). The template's `{{ i + 1 }}`
    // display, `currentPage === i` (0-based currentPage), the `change-page`
    // emit, and `i > 2` all assume 0-based `i`. Iterate explicit 0-based index
    // arrays instead of the bare Number so every one of those matches the
    // Webpack baseline's rendered output (render-output parity).
    pageIndexes() {
      const out = [];
      for (let n = 0; n < this.pages; n += 1) out.push(n);
      return out;
    },
    countIndexes() {
      const out = [];
      for (let n = 0; n < this.count; n += 1) out.push(n);
      return out;
    },
    moreClasses() {
      if (this.count > 8) {
        return 'more more-with-scroll';
      }

      return 'more';
    },
  },
  methods: {
    buttonStyles(isActive) {
      return {
        borderColor: this.theme.accent,
        color: isActive ? '#fff' : this.theme.accent,
        backgroundColor: isActive ? this.theme.accent : '#fff',
      };
    },

    moreButtonStyles(isActive) {
      return {
        // check for light mode since we can't use white for the text color, we'll settle for the accent color
        color: isActive && this.theme.type !== 'light' ? this.theme.background : this.theme.accent,
        backgroundColor: isActive ? '#f5f5f5' : null,
      };
    },

    toggleMorePages() {
      this.showMorePages = !this.showMorePages;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/_helpers';

.pagination {
  &__list {
    margin: 0;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 10px;
  }
  &__list-item {
    list-style: none;
    padding: 0 0.25rem;
    // position: relative;
  }
  &__button {
    border: 2px solid #532AA2;
    padding: 6px 16px;
    color: #532AA2;
    background: #fff;
    border-radius: 8px;
    font-size: 16px;
    &:hover {
      cursor: pointer;
    }
    &--active {
      background: #532AA2;
      color: #fff;
    }
  }

  .more {
    position: absolute;
    max-height: 200px;
    bottom: 80px;
    display: flex;
    align-items: center;
    flex-direction: column;
    border-radius: 0.5rem;
    border: 1px solid #f5f5f5;
    background: #FFFFFF;
    z-index: 300;
    box-shadow: 0px 1px 3px rgba(34, 32, 70, 0.2);
    overflow-y: hidden;
    &__list-item {
      list-style: none;
      width: 100%;
    }
    &__button {
      width: 100%;
      // @NOTE: if we see a gap in the scroll bar, we can use this
      // width: 101%;
      padding: 0.625rem 1.625rem;
      border: 0;
      font-weight: 500;
      background: #FFFFFF;
      &:hover {
        background: #f5f5f5;
        cursor: pointer;
      }
    }
  }

  .more-with-scroll {
    overflow-y: scroll;
    // @NOTE: if we see a gap in the scroll bar, we can use this
    // overflow-x: hidden;
  }
}
</style>
